import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { fetchServiceCategories } from '@/api/serviceApi';
import {
  FetchServiceCategoriesParams,
  FetchServiceCategoriesResponse,
} from '@/types/api/serviceApi';
import { hidePreloader, showPreloader } from '@/utils/preloader';

type UseFetchServiceCategoriesParams = FetchServiceCategoriesParams & {
  /** 画面側でスケルトン表示する場合は全画面プリローダーを出さない */
  disablePreloader?: boolean;
};

export const useFetchServiceCategories = ({
  m_affiliate_id,
  disablePreloader = false,
}: UseFetchServiceCategoriesParams) => {
  const [data, setData] = useState<FetchServiceCategoriesResponse['data']>();
  const [categories, setCategories] =
    useState<
      FetchServiceCategoriesResponse['data']['m_mt_service_categories']
    >();
  const [error, setError] = useState<AxiosError>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // 法人IDが未確定（顧客情報の取得前は 0）のうちは投げない。
    // 投げると空の応答が返り、それが後着すると空表示のまま固定されてしまう。
    if (!m_affiliate_id) return;

    // 法人IDが変わると再取得が走る。前のリクエストが後から返って
    // 新しい結果を上書きしないよう、古い応答は破棄する。
    let isCurrent = true;

    setIsLoading(true);
    if (!disablePreloader) showPreloader();

    const fn = async () => {
      try {
        const res = await fetchServiceCategories({ m_affiliate_id });
        if (!isCurrent) return;
        const data = res.data;

        const serviceCategories = data.m_mt_service_categories;

        setData(data);
        setCategories(serviceCategories);
      } catch (err) {
        if (isCurrent) setError(err as AxiosError);
      } finally {
        if (isCurrent) setIsLoading(false);
        if (!disablePreloader) hidePreloader();
      }
    };

    fn();

    return () => {
      isCurrent = false;
    };
  }, [m_affiliate_id, disablePreloader]);

  return {
    data,
    error,
    categories,
    isLoading,
  };
};
